import { TheNightlySection } from '@news-mono/common'
import { Topic, AdTargeting, Features } from '@news-mono/web-common'
import {
    FeatureState,
    isFeatureEnabled,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { layout } from '../../../App.routing'
import { sectionNavItems } from '../../../navigation'
import { cardList } from '../../home/get-cardList-segment'

export const getParentTopicComposition = ({
    section,
    topic,
    adTargeting,
    contextId,
    featureState,
}: {
    section: Exclude<TheNightlySection, 'default'>
    topic: Topic
    adTargeting: AdTargeting
    contextId: string
    featureState: FeatureState
}) => {
    const sectionNavItem = sectionNavItems[section]

    const subTopics =
        sectionNavItem.subNavLinks?.filter((subTopic) => {
            if (subTopic.link === sectionNavItem.link) {
                return false
            }
            if (subTopic.feature) {
                return isFeatureEnabled<Features>(
                    featureState,
                    subTopic.feature,
                    false,
                )
            }

            return true
        }) ?? []

    return [
        // top section
        layout.nestedComposition({
            type: 'luna',
            props: {
                dividerConfig: {
                    column: {
                        hasAsideAndAfterAsideDivider: false,
                    },
                    split: {
                        hasBeforeAsideAndAfterAsideDivider: false,
                    },
                },
            },
            contentAreas: {
                // Main content
                beforeAside: [
                    layout.component({
                        type: 'topic-section-card-list',
                        props: {
                            dataDefinitionArgs: {
                                type: 'context',
                                contextId,
                                section: section,
                            },
                            expectedItemCount: 1,
                            adUnits: 3,
                            adTargeting,
                            startIndex: 0,
                            endIndex: 1,
                            hasAfterAside: false,
                        },
                    }),
                ],
                // side small cards
                aside: [
                    layout.nestedComposition({
                        type: 'box',
                        props: {
                            stickyOffset:
                                'calc(var(--stickyHeaderHeight) + 80px)',
                        },
                        contentAreas: {
                            main: [
                                cardList({
                                    data: {
                                        type: 'context',
                                        contextId,
                                        section: section,
                                    },
                                    heading: 'Latest',
                                    numbered: false,
                                    hideMedia: true,
                                    adNoticePosition: 'above-center',
                                    startIndex: 1,
                                    headerColorOverride: 'inherit',
                                }),

                                layout.component({
                                    type: 'ad-unit',
                                    props: {
                                        adType: 'inline',
                                        noticePosition: 'above-center',
                                        slot: {
                                            id: 'sidebar',
                                            size: 'above1280MrecLeaderboardAbove768MrecBelow',
                                        },
                                    },
                                }),
                            ],
                        },
                    }),
                ],
                afterAside: [],
            },
        }),

        // sub topic sections
        ...subTopics.flatMap(({ name, link }, index, allSubTopics) => {
            const components = [
                layout.component({
                    type: 'tn-article-cards',
                    props: {
                        sectionHeading: name,
                        headerAs: 'h2',
                        headerLink: link,
                        useSectionColor: true,
                        dataDefinitionArgs: {
                            type: 'listing',
                            topics: [link.replace(/^\//, '')],
                            includeSubTopics: false,
                            paging: {
                                page: 1,
                                pageSize: 4,
                            },
                        },
                    },
                }),
            ]

            // Insert ad after every 3rd sub topic, starting after the second one, but not after the last one
            const shouldInsertAd =
                index >= 1 &&
                (index - 1) % 3 === 0 &&
                index < allSubTopics.length - 1

            if (shouldInsertAd) {
                const adNumber = Math.floor((index - 1) / 3) + 1

                components.push(
                    layout.component({
                        type: 'ad-unit',
                        props: {
                            adType: 'inline',
                            noticePosition: 'above-center',
                            slot: {
                                id: `topic-section-${adNumber}`,
                                size: 'leaderboardAbove768MrecBelow',
                            },
                        },
                    }),
                )
            }

            return components
        }),
    ]
}
